/**
 * Field-Sanity Validator · Phase E v8b · 2026-02-28
 * =================================================
 *
 * Pure functions that gate pass-vector data quality. Used by
 * PassVectorRow to render the ⚠ "IMPOSSIBLE DISTANCE" warning
 * badge when a saved pass exceeds the physical diagonal of the
 * active field profile.
 *
 *      max_allowed = sqrt(L² + W²) + tolerance
 *
 * Mirror of ``modules/field_specs.py::max_allowed_pass_length_m``
 * — keep the two in sync. We compute on the frontend because the
 * UI badge needs zero-latency rendering on every row, and adding a
 * backend round-trip per row would burn budget for what is just
 * Pythagoras.
 */

/**
 * Max physical pass length allowed inside a field of given
 * dimensions. Defaults to WFDF 100×37 m for sequences uploaded
 * before Phase E v7 (no game_config.field_*_m). Tolerance buffer
 * absorbs:
 *  - pulls / catches a few metres behind the endzone corner
 *  - calibration reprojection error (~0.5–1.0 m typical)
 *  - operator clicking just outside the line on edge pins
 */
export function maxAllowedPassLengthM(
  { fieldLengthM, fieldWidthM } = {},
  toleranceM = 5.0,
) {
  const L =
    typeof fieldLengthM === "number" && fieldLengthM > 0
      ? fieldLengthM
      : 100.0;
  const W =
    typeof fieldWidthM === "number" && fieldWidthM > 0 ? fieldWidthM : 37.0;
  return Math.sqrt(L * L + W * W) + Number(toleranceM || 0);
}

/**
 * `true` when the measured pass distance exceeds the field diagonal
 * (plus tolerance). Used by the row badge to decide whether to flash
 * the impossible-distance warning. Returns `false` on non-numeric /
 * NaN inputs so the badge never flickers on partially-hydrated vectors.
 */
export function isImpossibleDistance(distanceM, dims = {}, toleranceM = 5.0) {
  if (typeof distanceM !== "number" || !Number.isFinite(distanceM)) {
    return false;
  }
  return distanceM > maxAllowedPassLengthM(dims, toleranceM);
}
